import {
  MdbaseBrowserSelection,
  MdbaseConnect,
  type ConnectOutcome,
  type JsonObject,
  type MdbaseConnection,
  type MdbaseConnectionInfo
} from "@mdbase-dev/connect";
import "./styles.css";

const serverUrl = "https://connect-staging.mdbase.dev";
const mode = document.body.dataset.probe === "mutation" ? "mutation" : "read";
const title = mode === "mutation" ? "Mutation + file probe" : "Read + query probe";
const historyKey = `mdbase-staging-${mode}-probe-log`;
const app = document.querySelector<HTMLElement>("#app");
if (!app) throw new Error("Probe root is missing.");

app.innerHTML = `
  <header>
    <p class="eyebrow">mdbase staging lab</p>
    <h1>${title}</h1>
    <p>Independent application identity · <code>${serverUrl}</code></p>
  </header>
  <section class="panel">
    <div class="section-heading"><h2>Session</h2><span id="route" class="badge">opening</span></div>
    <pre id="snapshot" aria-label="Session snapshot"></pre>
    <div id="collections" class="actions"></div>
    <div class="actions">
      <button id="authorize" type="button">Authorize a collection</button>
      <button id="check-direct" type="button" disabled>Enable direct route</button>
      <button id="disable-direct" type="button" disabled>Force relay route</button>
    </div>
  </section>
  <section class="panel">
    <div class="section-heading"><h2>Scenarios</h2><span class="badge">${mode}</span></div>
    <div class="actions">
      <button id="run-suite" class="primary" type="button" disabled>Run ${mode} suite</button>
      <button id="watch" type="button" disabled>Watch for 30 seconds</button>
      <button id="clear-log" type="button">Clear log</button>
    </div>
  </section>
  <section class="panel log-panel">
    <div class="section-heading"><h2>Evidence log</h2><span id="run-state" class="badge">idle</span></div>
    <pre id="log" aria-live="polite"></pre>
  </section>
`;

const snapshotElement = required("snapshot");
const routeElement = required("route");
const collectionsElement = required("collections");
const logElement = required("log");
const runStateElement = required("run-state");
const authorizeButton = requiredButton("authorize");
const directButton = requiredButton("check-direct");
const disableDirectButton = requiredButton("disable-direct");
const suiteButton = requiredButton("run-suite");
const watchButton = requiredButton("watch");
const clearLogButton = requiredButton("clear-log");

let entries = loadLog();
let running = false;
renderLog();

const connect = new MdbaseConnect<JsonObject>({
  serverUrl,
  manifest: new URL("/.well-known/mdbase-app.json", location.origin).href,
  redirectUri: new URL("/", location.origin).href,
  loopbackUrl: "http://127.0.0.1:28486"
});
const session = connect.application({
  selection: new MdbaseBrowserSelection({ fallbackPath: "/" }),
  autoSelect: "only"
});

session.subscribe(renderSession);
renderSession();

authorizeButton.addEventListener("click", () => run("authorize", async () => {
  const result = await session.authorize("choose");
  return outcomeValue("authorize", result);
}));
directButton.addEventListener("click", () => run("request-direct", async () => {
  const connection = requireConnection();
  const direct = outcomeValue("request direct access", await connection.requestDirectAccess());
  return { direct, connection: connection.info() };
}));
disableDirectButton.addEventListener("click", () => {
  const connection = requireConnection();
  connection.disableDirectAccess();
  append("route", "Direct access disabled for this application session", connection.info());
  renderSession();
});
suiteButton.addEventListener("click", () => run(`${mode}-suite`, () =>
  mode === "mutation" ? runMutationSuite(requireConnection()) : runReadSuite(requireConnection())
));
watchButton.addEventListener("click", () => run("watch-30s", () => watchFor(requireConnection(), 30_000)));
clearLogButton.addEventListener("click", () => {
  entries = [];
  sessionStorage.removeItem(historyKey);
  renderLog();
});

void run("session-start", async () => outcomeValue("session start", await session.start()));

async function runReadSuite(connection: MdbaseConnection<JsonObject>) {
  const info = requireInfo(connection);
  append("suite", "Read suite started", info);
  const description = outcomeValue("describe", await connection.describe());
  append("pass", "Collection description", {
    route: connection.info()?.route,
    types: description.types.map((item) => item.name),
    contracts: description.contracts.map((item) => `${item.id}@${item.version}`),
    cursor: description.changeCursor
  });

  const query = outcomeValue("query", await connection.query({
    includeBody: true,
    frontmatterMode: "both",
    orderBy: [{ field: "file.mtime", direction: "desc" }],
    limit: 100
  }));
  append("pass", `Queried ${query.results.length} records`, query.results.map((record) => ({
    path: record.path,
    types: record.types,
    persisted: record.frontmatter,
    effective: record.effectiveFrontmatter,
    bodyBytes: new TextEncoder().encode(record.body ?? "").byteLength
  })));

  for (const record of query.results.slice(0, 10)) {
    const read = outcomeValue(`read ${record.path}`, await connection.read({
      path: record.path,
      includeDocument: true
    }));
    invariant(typeof read.document === "string", `Exact source missing for ${record.path}`);
    append("pass", `Read exact source: ${record.path}`, {
      revision: read.revision,
      sourceBytes: new TextEncoder().encode(read.document).byteLength,
      frontmatter: read.frontmatter
    });
  }

  const files = [];
  for await (const file of connection.files.list({ pageSize: 100 })) files.push(file);
  append("pass", `Listed ${files.length} files`, files);
  for (const file of files.filter((item) => item.size <= 2_000_000).slice(0, 20)) {
    const bytes = new Uint8Array(await (await connection.files.download(file)).arrayBuffer());
    invariant(bytes.byteLength === file.size, `Downloaded size differs for ${file.path}`);
    const digest = `sha256:${await sha256(bytes)}`;
    invariant(digest === file.contentDigest, `Downloaded digest differs for ${file.path}`);
    append("pass", `Downloaded and verified ${file.path}`, { size: file.size, digest });
  }

  await watchFor(connection, 2_000);
  append("suite", "Read suite completed", requireInfo(connection));
  return { records: query.results.length, files: files.length, route: connection.info()?.route };
}

async function runMutationSuite(connection: MdbaseConnection<JsonObject>) {
  const failures: string[] = [];
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  const folder = `staging-probes/${stamp}`;
  const originalPath = `${folder}/shape-matrix.md`;
  const renamedPath = `${folder}/shape-matrix-renamed.md`;
  append("suite", "Mutation suite started", { ...requireInfo(connection), folder });

  const frontmatter: JsonObject = {
    string_value: "colon: hash # café 東京 ✅",
    integer_value: 7,
    number_value: 42.5,
    boolean_true: true,
    boolean_false: false,
    null_value: null,
    empty_string: "",
    list_value: ["alpha", 2, false, null, { nested: "value" }],
    nested_value: { child: [{ id: "one", enabled: true }, { id: "two", enabled: false }] },
    "spaced key": "spaced value",
    large_value: "x".repeat(16_384)
  };
  const created = outcomeValue("create shaped record", await connection.create({
    path: originalPath,
    frontmatter,
    body: "# Shape matrix\n\nUnicode body: naïve résumé 東京 ✅\n",
    includeDocument: true
  }));
  invariant(created.frontmatter.null_value === null, "Null did not round-trip on create");
  invariant(Array.isArray(created.frontmatter.list_value), "List did not round-trip on create");
  append("pass", "Created varied record shape", summarizeRecord(created));

  const read = outcomeValue("read created shape", await connection.read({ path: originalPath, includeDocument: true }));
  invariant(deepEqual(read.frontmatter, frontmatter), "Created frontmatter changed on exact read");
  invariant(read.document?.includes("東京 ✅") === true, "Unicode source did not round-trip");
  append("pass", "Exact record read matches created shape", summarizeRecord(read));

  const updated = outcomeValue("patch record", await connection.update({
    path: originalPath,
    patch: { patched: true, integer_value: 8 },
    ifRevision: read.revision,
    includeDocument: true
  }));
  invariant(updated.frontmatter.patched === true && updated.frontmatter.integer_value === 8, "Patch was not applied");
  append("pass", "Patched record", summarizeRecord(updated));

  const stale = await connection.update({
    path: originalPath,
    patch: { stale_write_must_not_land: true },
    ifRevision: read.revision
  });
  invariant(!stale.ok, "A stale revision write unexpectedly succeeded");
  append("pass", "Stale revision was rejected", stale.problem);

  const validation = outcomeValue("validate record", await connection.validate({ path: originalPath }));
  append("pass", "Validated shaped record", validation);

  let preflight: unknown;
  try {
    preflight = outcomeValue("preflight rename", await connection.preflightRename({
      from: originalPath,
      to: renamedPath,
      ifRevision: updated.revision,
      updateRefs: true
    }));
  } catch (error) {
    failures.push("rename preflight");
    preflight = serializeError(error);
    append("fail", "Rename preflight failed; continuing independent scenarios", preflight);
  }
  const renamed = outcomeValue("rename record", await connection.rename({
    from: originalPath,
    to: renamedPath,
    ifRevision: updated.revision,
    updateRefs: true,
    includeDocument: true
  }));
  append("pass", "Renamed shaped record", { preflight, renamed: summarizeRecord(renamed) });

  const disposable = outcomeValue("create disposable record", await connection.create({
    path: `${folder}/delete-me.md`,
    frontmatter: { purpose: "delete-probe" },
    body: "Disposable.\n"
  }));
  let deletePreview: unknown;
  try {
    deletePreview = outcomeValue("preflight delete", await connection.preflightDelete({
      path: disposable.path,
      ifRevision: disposable.revision,
      checkBacklinks: true
    }));
  } catch (error) {
    failures.push("delete preflight");
    deletePreview = serializeError(error);
    append("fail", "Delete preflight failed; continuing independent scenarios", deletePreview);
  }
  const deleted = outcomeValue("delete record", await connection.delete({
    path: disposable.path,
    ifRevision: disposable.revision,
    checkBacklinks: true
  }));
  invariant(deleted.deleted, "Disposable record was not deleted");
  append("pass", "Deleted disposable record", { deletePreview, deleted });

  const uploaded = [];
  for (const fixture of fileFixtures()) {
    const path = `${folder}/${fixture.name}`;
    const descriptor = await connection.files.upload(path, new Blob([fixture.bytes], { type: fixture.mediaType }), {
      mediaType: fixture.mediaType
    });
    const downloaded = new Uint8Array(await (await connection.files.download(descriptor)).arrayBuffer());
    invariant(equalBytes(downloaded, fixture.bytes), `File bytes changed for ${fixture.name}`);
    uploaded.push(descriptor);
    append("pass", `Uploaded and verified ${fixture.name}`, descriptor);
  }

  const binary = uploaded.find((item) => item.path.endsWith("sample.bin"));
  if (!binary) throw new Error("Binary file descriptor missing");
  const replacement = Uint8Array.from([255, 0, 127, 64, 10, 13, 42]);
  const replaced = await connection.files.upload(binary.path, new Blob([replacement]), {
    mediaType: "application/octet-stream",
    ifRevision: binary.revision
  });
  const moved = await connection.files.move(replaced, `${folder}/sample-moved.bin`, { ifRevision: replaced.revision });
  const removed = await connection.files.delete(moved, { ifRevision: moved.revision });
  append("pass", "Replaced, moved, and deleted binary file", { replaced, moved, removed });

  append("suite", "Mutation suite completed", {
    route: connection.info()?.route,
    retainedRecord: renamedPath,
    retainedFiles: uploaded.filter((item) => item.fileId !== binary.fileId).map((item) => item.path)
  });
  if (failures.length) throw new Error(`Suite completed with failed scenarios: ${failures.join(", ")}`);
  return { route: connection.info()?.route, folder, uploaded: uploaded.length };
}

async function watchFor(connection: MdbaseConnection<JsonObject>, durationMs: number) {
  const lifetime = new AbortController();
  const outcome = await connection.watch({ lifetimeSignal: lifetime.signal });
  const subscription = outcomeValue("start watch", outcome);
  const events: unknown[] = [];
  const statuses: unknown[] = [subscription.status];
  const problems: unknown[] = [];
  const unsubscribe = subscription.subscribe(
    (change) => { events.push(change); append("watch", "Collection change", change); },
    (status) => { statuses.push(status); },
    (problem) => { problems.push(problem); }
  );
  await new Promise((resolve) => window.setTimeout(resolve, durationMs));
  unsubscribe();
  lifetime.abort();
  subscription.close();
  append("pass", `Watch stayed open for ${durationMs} ms`, { statuses, events: events.length, problems });
  invariant(problems.length === 0, "Watch reported a problem");
  return { statuses, events };
}

function fileFixtures() {
  const encoder = new TextEncoder();
  return [
    { name: "sample.txt", mediaType: "text/plain; charset=utf-8", bytes: encoder.encode("plain text café 東京 ✅\n") },
    { name: "sample.json", mediaType: "application/json", bytes: encoder.encode('{"string":"café 東京","number":42.5,"boolean":false,"null":null,"list":[1,"two"]}\n') },
    { name: "sample.yaml", mediaType: "application/yaml", bytes: encoder.encode("string: café 東京\nnumber: 42.5\nboolean: false\nnull: null\nlist: [1, two]\n") },
    { name: "sample.png", mediaType: "image/png", bytes: base64Bytes("iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=") },
    { name: "sample.pdf", mediaType: "application/pdf", bytes: encoder.encode("%PDF-1.4\n1 0 obj<</Type/Catalog>>endobj\ntrailer<</Root 1 0 R>>\n%%EOF\n") },
    { name: "sample.bin", mediaType: "application/octet-stream", bytes: Uint8Array.from([0, 1, 2, 3, 127, 128, 254, 255, 10, 13]) }
  ];
}

function renderSession() {
  const snapshot = session.getSnapshot();
  snapshotElement.textContent = pretty(snapshot);
  const ready = snapshot.status === "ready";
  suiteButton.disabled = !ready || running;
  watchButton.disabled = !ready || running;
  directButton.disabled = !ready || running;
  disableDirectButton.disabled = !ready || running;
  authorizeButton.disabled = running;
  routeElement.textContent = ready ? snapshot.info.route : snapshot.status;
  routeElement.dataset.route = ready ? snapshot.info.route : "";
  collectionsElement.replaceChildren();
  for (const collection of snapshot.connections) {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = `${collection.displayName} · ${collection.authority.kind} · ${collection.route}`;
    button.disabled = running || ("collectionId" in snapshot && snapshot.collectionId === collection.collectionId);
    button.addEventListener("click", () => {
      const selected = session.select(collection.collectionId, { history: "replace" });
      if (!selected.ok) append("fail", "Could not select collection", selected.problem);
      renderSession();
    });
    collectionsElement.append(button);
  }
}

async function run(label: string, operation: () => Promise<unknown>) {
  if (running) return;
  running = true;
  runStateElement.textContent = label;
  runStateElement.dataset.state = "running";
  renderSession();
  append("start", label);
  try {
    const result = await operation();
    append("complete", label, result);
  } catch (error) {
    append("fail", label, serializeError(error));
  } finally {
    running = false;
    runStateElement.textContent = "idle";
    runStateElement.dataset.state = "idle";
    renderSession();
  }
}

function outcomeValue<T>(label: string, outcome: ConnectOutcome<T>): T {
  if (!outcome.ok) throw new Error(`${label}: ${outcome.problem.code}: ${outcome.problem.message}`);
  return outcome.value;
}

function requireConnection() {
  const connection = session.connection();
  if (!connection) throw new Error("Select an authorized, ready collection first.");
  return connection;
}

function requireInfo(connection: MdbaseConnection<JsonObject>): MdbaseConnectionInfo {
  const info = connection.info();
  if (!info) throw new Error("The selected collection is not authorized.");
  return info;
}

function summarizeRecord(record: { path: string; revision: string; frontmatter: JsonObject; document?: string }) {
  return {
    path: record.path,
    revision: record.revision,
    frontmatter: record.frontmatter,
    sourceBytes: new TextEncoder().encode(record.document ?? "").byteLength
  };
}

function append(kind: string, message: string, details?: unknown) {
  entries.push({ at: new Date().toISOString(), kind, message, ...(details === undefined ? {} : { details }) });
  if (entries.length > 500) entries = entries.slice(-500);
  sessionStorage.setItem(historyKey, JSON.stringify(entries));
  renderLog();
}

function loadLog(): Array<Record<string, unknown>> {
  try {
    const value = JSON.parse(sessionStorage.getItem(historyKey) ?? "[]") as unknown;
    return Array.isArray(value) ? value as Array<Record<string, unknown>> : [];
  } catch {
    return [];
  }
}

function renderLog() {
  logElement.textContent = entries.map((entry) => pretty(entry)).join("\n");
  logElement.scrollTop = logElement.scrollHeight;
}

function serializeError(error: unknown) {
  if (error && typeof error === "object") {
    const candidate = error as { name?: string; message?: string; stack?: string; problem?: unknown };
    return { name: candidate.name, message: candidate.message, problem: candidate.problem, stack: candidate.stack };
  }
  return String(error);
}

function invariant(condition: unknown, message: string): asserts condition {
  if (!condition) throw new Error(message);
}

function deepEqual(left: unknown, right: unknown) {
  return JSON.stringify(canonicalize(left)) === JSON.stringify(canonicalize(right));
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => [key, canonicalize(item)]));
  }
  return value;
}

function equalBytes(left: Uint8Array, right: Uint8Array) {
  return left.byteLength === right.byteLength && left.every((byte, index) => byte === right[index]);
}

async function sha256(bytes: Uint8Array) {
  const digest = await crypto.subtle.digest("SHA-256", bytes as BufferSource);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function base64Bytes(value: string) {
  return Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
}

function pretty(value: unknown) {
  return JSON.stringify(value, (_key, item) => {
    if (typeof item === "string" && item.length > 500) return `${item.slice(0, 120)}…[${item.length} chars]`;
    return item;
  }, 2);
}

function required(id: string) {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing #${id}`);
  return element;
}

function requiredButton(id: string) {
  const element = required(id);
  if (!(element instanceof HTMLButtonElement)) throw new Error(`#${id} is not a button`);
  return element;
}
